
/**
 * This file contains central configuration values for the game's economy and progression.
 */

// The maximum amount of gold a player can have at the start of their turn.
export const MAX_GOLD = 10;

// The base amount of gold added to the round number to determine turn gold.
// e.g., round 1 gold = 1 + 2 = 3. round 5 gold = 5 + 2 = 7.
export const BASE_GOLD_PER_TURN = 2;

// An array where the index represents the TIER to upgrade TO, and the value is the cost.
// Index 0 is unused. Index 2 is the cost to upgrade from Tier 1 to Tier 2.
export const TAVERN_UPGRADE_COSTS: number[] = [
    0, // Unused
    0, // Cost to get to Tier 1 (it's free)
    5, // Cost to upgrade to Tier 2
    7, // Cost to upgrade to Tier 3
    8, // Cost to upgrade to Tier 4
    9, // Cost to upgrade to Tier 5
    10, // Cost to upgrade to Tier 6
];

// Tavern tier roll probability table
// Each tavern tier maps to an array of { tier, weight } objects
export const TAVERN_ROLL_TABLE: Record<number, { tier: number; weight: number }[]> = {
    1: [{ tier: 1, weight: 100 }],
    2: [
        { tier: 1, weight: 70 },
        { tier: 2, weight: 30 }
    ],
    3: [
        { tier: 1, weight: 55 },
        { tier: 2, weight: 35 },
        { tier: 3, weight: 10 }
    ],
    4: [
        { tier: 1, weight: 35 },
        { tier: 2, weight: 35 },
        { tier: 3, weight: 25 },
        { tier: 4, weight: 5 }
    ],
    5: [
        { tier: 1, weight: 20 },
        { tier: 2, weight: 25 },
        { tier: 3, weight: 35 },
        { tier: 4, weight: 15 },
        { tier: 5, weight: 5 }
    ],
    6: [
        { tier: 1, weight: 10 },
        { tier: 2, weight: 15 },
        { tier: 3, weight: 30 },
        { tier: 4, weight: 25 },
        { tier: 5, weight: 15 },
        { tier: 6, weight: 5 }
    ]
};

/**
 * Rolls a card tier based on the player's tavern tier using weighted probabilities.
 * @param tavernTier The player's current tavern tier (1-6)
 * @param rng Optional random number generator (returns 0-1), defaults to Math.random
 * @returns The rolled card tier
 */
export function rollCardTier(tavernTier: number, rng: () => number = Math.random): number {
    // Clamp tavern tier to valid range
    const validTavernTier = Math.max(1, Math.min(6, tavernTier));
    const rollTable = TAVERN_ROLL_TABLE[validTavernTier];

    // Calculate total weight
    const totalWeight = rollTable.reduce((sum, entry) => sum + entry.weight, 0);

    // Roll random number
    let roll = rng() * totalWeight;

    // Find which tier was rolled
    for (const entry of rollTable) {
        roll -= entry.weight;
        if (roll <= 0) {
            return entry.tier;
        }
    }

    // Fallback (should never reach here)
    return rollTable[rollTable.length - 1].tier;
}
